import { isValidKatakana, optionalFixedDigits, optionalRangeDigits } from '@/utils';
import { z } from 'zod';

/** 住所2 + 住所3 may use at most this many characters combined (Shinsa m_customer.ADDR_2). */
const ADDRESS_TOTAL_MAX_LENGTH = 100;

/**
 * Custom date schema that handles Dayjs objects, Date objects, and strings
 */
const dateSchema = z
  .any()
  .optional()
  .transform(val => {
    if (!val) return null;

    // Handle Dayjs objects
    if (typeof val === 'object' && val !== null && typeof val.format === 'function') {
      return val.format('YYYY-MM-DD');
    }

    // Handle Date objects
    if (val instanceof Date) {
      return val.toISOString().split('T')[0];
    }

    // Handle strings
    if (typeof val === 'string') {
      if (val === '') return null;
      return val;
    }

    return val;
  });

/**
 * Custom time schema that handles Dayjs objects
 */
const timeSchema = z
  .any()
  .optional()
  .transform(val => {
    if (!val) return null;

    // Handle Dayjs objects
    if (typeof val === 'object' && val !== null && typeof val.format === 'function') {
      return val.format('HH:mm');
    }

    // Handle strings
    if (typeof val === 'string') {
      if (val === '') return null;
      return val;
    }

    return val;
  });

/**
 * Custom year-month schema that handles Dayjs objects and transforms them to YYYYMM
 */
const yearMonthSchema = z
  .any()
  .optional()
  .transform(val => {
    if (!val) return null;

    // Handle Dayjs objects
    if (typeof val === 'object' && val !== null && typeof val.format === 'function') {
      return Number(val.format('YYYYMM'));
    }

    // Handle strings
    if (typeof val === 'string') {
      if (val === '') return null;
      // If it's already YYYYMM string, convert to Number
      if (/^\d{6}$/.test(val)) return Number(val);
      // If it's YYYY-MM, convert to YYYYMM number
      if (/^\d{4}-\d{2}$/.test(val)) return Number(val.replace(/-/g, ''));
      // If it's YYYY-MM-DD, convert to YYYYMM number
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return Number(val.replace(/-/g, '').substring(0, 6));
      return val;
    }

    // Handle numbers
    if (typeof val === 'number') {
      return val;
    }

    return val;
  });

/**
 * Clinic Info Schema - 治療院情報作成
 * Mapping to: Basic Information, Address Information, Contact Information, Business Hours
 */
export const clinicInfoSchema = z
  .object({
    clinic_id: z.coerce.number().min(1, '治療院IDは必須です。'),
    // Basic Information
    name: z
      .string()
      .trim()
      .min(1, '治療院名は必須です。')
      .max(100, '治療院名は100文字以下にしてください。'),
    name_kana: z
      .string()
      .nullable()
      .optional()
      .transform(val => val ?? '')
      .transform(val => val.trim())
      .default(''),
    available_from: dateSchema.refine(val => !!val, {
      message: '適用開始日は必須です。',
    }),
    available_to: dateSchema,
    sub_number: z.coerce.number().optional(),

    // Address Information
    postal_code: z
      .string()
      .trim()
      .min(1, '郵便番号は必須です。')
      .length(7, '郵便番号は7桁で入力してください。')
      .regex(/^\d{7}$/, '郵便番号は7桁で入力してください。'),
    prefecture_id: z.any().refine(val => !!val && !isNaN(Number(val)) && Number(val) >= 1, {
      message: '都道府県は必須です。',
    }),
    address1: z
      .string()
      .trim()
      .min(1, '住所1は必須です。')
      .max(100, '住所1は100文字以下で入力してください。'),
    // Length is not checked per box: 住所2 and 住所3 are concatenated into a single Shinsa column
    // (m_customer.ADDR_2) when synced, so the limit applies to their total — see the superRefine below.
    address2: z.string().trim().min(1, '住所2は必須です。'),
    address3: z.string().trim().optional().nullable(),

    // Contact Information
    telephone: z
      .string()
      .trim()
      .max(13, '電話番号は13文字以下で入力してください。')
      .optional()
      .nullable(),
    fax: z.string().trim().max(13, 'FAX番号は13文字以下で入力してください。').optional().nullable(),
    mobile: z
      .string()
      .trim()
      .max(13, '携帯電話番号は13文字以下で入力してください。')
      .optional()
      .nullable(),
    email: z
      .string()
      .trim()
      .max(320, 'メールアドレスは320文字以内で入力してください。')
      .email('メールアドレスを正しいメールアドレスにしてください。')
      .optional()
      .or(z.literal(''))
      .nullable(),

    // Business Hours
    open_time_from: timeSchema,
    open_time_to: timeSchema,
    open_week: z.array(z.coerce.number()).optional().nullable(),
  })
  .superRefine((data, ctx) => {
    // name_kana validation is handled here so it does not short-circuit other superRefine checks.
    const nameKana = (data.name_kana ?? '').trim();
    if (nameKana !== '' && !isValidKatakana(nameKana)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '治療院カナの書式が正しくありません。',
        path: ['name_kana'],
      });
    }
    if (nameKana.length > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '治療院カナは100文字以下にしてください。',
        path: ['name_kana'],
      });
    }

    // 住所2 + 住所3 land in one Shinsa column (m_customer.ADDR_2, varchar(100)) as a plain
    // concatenation, so the pair is capped together instead of box by box. The message goes under
    // whichever box holds more text (住所2 when tied), so it points at the one to shorten first.
    const address2Length = data.address2.length;
    const address3Length = (data.address3 ?? '').trim().length;
    if (address2Length + address3Length > ADDRESS_TOTAL_MAX_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '住所入力欄に入力する文字数の合計は、100文字未満にしてください。',
        path: [address3Length > address2Length ? 'address3' : 'address2'],
      });
    }

    // Date Comparison
    if (data.available_from && data.available_to) {
      const fromD = new Date(data.available_from);
      const toD = new Date(data.available_to);
      if (toD < fromD) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '適用終了日時は適用開始日時より後の日時を指定してください。',
          path: ['available_to'],
        });
      }
    }

    // Phone validation
    if (!data.telephone && !data.mobile) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'どちらかに電話番号を入力してください。',
        path: ['telephone'],
      });
    }

    if (!data.telephone && !data.mobile) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: '',
        path: ['mobile'],
      });
    }

    // Time Comparison
    if (data.open_time_from && data.open_time_to) {
      if (data.open_time_to <= data.open_time_from) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: '営業時間TOは営業時間FROMより後の時間を指定してください。',
          path: ['open_time_to'],
        });
      }
    }
  });

/**
 * Clinic Payment Info Schema - レセプト印字支払機関欄情報
 * Mapping to: Bank Transfer Information (Primary)
 */
export const clinicPaymentInfoSchema = z.object({
  bank_payment_type_id: z.coerce.number().optional().nullable(),
  bank_code: optionalRangeDigits(
    3,
    4,
    '振込先金融機関コードは数字にしてください。',
    '振込先金融機関コードは3〜4桁にしてください。'
  ),
  bank_type_id: z.coerce.number().optional().nullable(),
  bank_name: z
    .string()
    .trim()
    .max(100, '振込先銀行名は100文字以下にしてください。')
    .optional()
    .nullable(),
  bank_name_kana: z
    .string()
    .nullable()
    .optional()
    .transform(val => val ?? '')
    .refine(val => val === '' || (val.trim() !== '' && isValidKatakana(val)), {
      message: 'カナ: 振込先銀行名カナの書式が正しくありません。',
    })
    .transform(val => val.trim())
    .refine(val => val.length <= 100, {
      message: '振込先銀行名カナは100文字以下にしてください。',
    })
    .default(''),
  bank_name_suffix: z
    .string()
    .trim()
    .max(100, '銀行名追加文言は100文字以下にしてください。')
    .optional()
    .nullable(),
  bank_branch_code: optionalFixedDigits(
    3,
    '振込先支店コードは数字にしてください。',
    '振込先支店コードは3桁にしてください。'
  ),
  bank_branch_type_id: z.coerce.number().optional().nullable(),
  bank_branch_name: z
    .string()
    .trim()
    .max(100, '振込先支店名は100文字以下にしてください。')
    .optional()
    .nullable(),
  bank_branch_name_kana: z
    .string()
    .nullable()
    .optional()
    .transform(val => val ?? '')
    .refine(val => val === '' || (val.trim() !== '' && isValidKatakana(val)), {
      message: 'カナ: 振込先支店名カナの書式が正しくありません。',
    })
    .transform(val => val.trim())
    .refine(val => val.length <= 100, {
      message: '振込先支店名カナは100文字以下にしてください。',
    })
    .default(''),
  branch_branch_name_suffix: z
    .string()
    .trim()
    .max(100, '支店名追加文言は100文字以下にしてください。')
    .optional()
    .nullable(),
  bank_account_type_id: z.coerce.number().optional().nullable(),
  account_number: optionalRangeDigits(
    5,
    7,
    '振込先口座番号は数字にしてください。',
    '振込先口座番号は5〜7桁にしてください。'
  ),
  account_name: z
    .string()
    .trim()
    .max(100, '振込先口座名義は100文字以下にしてください。')
    .optional()
    .nullable(),
  account_name_kana: z
    .string()
    .nullable()
    .optional()
    .transform(val => val ?? '')
    .refine(val => val === '' || (val.trim() !== '' && isValidKatakana(val)), {
      message: 'カナ: 振込先口座名義カナの書式が正しくありません。',
    })
    .transform(val => val.trim())
    .refine(val => val.length <= 100, {
      message: '振込先口座名義カナは100文字以下にしてください。',
    })
    .default(''),
});

/**
 * Authorized Receipt Schema - レセプト印字委任欄代理人情報
 * Mapping to: Representative/Proxy Information
 */
export const authorizedReceiptSchema = z.object({
  representative_name: z
    .string()
    .trim()
    .max(100, '代理人氏名は100文字以下にしてください。')
    .optional()
    .nullable(),
  representative_address: z
    .string()
    .trim()
    .max(100, '代理人住所は100文字以下にしてください。')
    .optional()
    .nullable(),
  representative_telephone: z
    .string()
    .trim()
    .max(100, '代理人電話番号は100文字以下にしてください。')
    .optional()
    .nullable(),
});

/**
 * Receipt Fee Transfer Schema - レセプト印字費用振替情報
 * Mapping to: Reservation Transfer Information (Secondary) & Administrative Info
 */
export const receiptFeeTransferSchema = z.object({
  transfer_bank_code: optionalRangeDigits(
    3,
    4,
    '金融機関コードは数字にしてください。',
    '金融機関コードは3〜4桁にしてください。'
  ),
  transfer_bank_name: z
    .string()
    .trim()
    .max(100, '銀行名は100文字以下にしてください。')
    .optional()
    .nullable(),
  transfer_bank_name_kana: z
    .string()
    .nullable()
    .optional()
    .transform(val => val ?? '')
    .refine(val => val === '' || (val.trim() !== '' && isValidKatakana(val)), {
      message: 'カナ: 銀行名カナの書式が正しくありません。',
    })
    .transform(val => val.trim())
    .refine(val => val.length <= 100, {
      message: '銀行名カナは100文字以下にしてください。',
    })
    .default(''),
  transfer_bank_branch_code: optionalFixedDigits(
    3,
    '支店番号は数字にしてください。',
    '支店番号は3桁にしてください。'
  ),
  transfer_bank_branch_name: z
    .string()
    .trim()
    .max(100, '支店名は100文字以下にしてください。')
    .optional()
    .nullable(),
  transfer_bank_branch_name_kana: z
    .string()
    .nullable()
    .optional()
    .transform(val => val ?? '')
    .refine(val => val === '' || (val.trim() !== '' && isValidKatakana(val)), {
      message: 'カナ: 支店カナの書式が正しくありません。',
    })
    .transform(val => val.trim())
    .refine(val => val.length <= 100, {
      message: '支店カナは100文字以下にしてください。',
    })
    .default(''),
  transfer_bank_account_type_id: z.coerce.number().optional().nullable(),
  transfer_bank_account_number: optionalRangeDigits(
    5,
    7,
    '口座番号は数字にしてください。',
    '口座番号は5〜7桁にしてください。'
  ),
  transfer_bank_account_name: z
    .string()
    .trim()
    .max(100, '口座名義は100文字以下にしてください。')
    .optional()
    .nullable(),
  transfer_bank_account_name_kana: z
    .string()
    .nullable()
    .optional()
    .transform(val => val ?? '')
    .refine(val => val === '' || (val.trim() !== '' && isValidKatakana(val)), {
      message: 'カナ: 口座名義カナの書式が正しくありません。',
    })
    .transform(val => val.trim())
    .refine(val => val.length <= 100, {
      message: '口座名義カナは100文字以下にしてください。',
    })
    .default(''),
  billing_company_code: optionalFixedDigits(
    6,
    '請求先企業番号は数字にしてください。',
    '請求先企業番号は6桁にしてください。'
  ),
  customer_code: optionalRangeDigits(
    5,
    12,
    '顧客番号は数字にしてください。',
    '顧客番号は5〜12桁にしてください。'
  ),
  is_excluded_transfer: z.boolean().optional().nullable(),
  is_required_billing: z.boolean().optional().nullable(),
});

/**
 * Other Info Schema - その他情報
 * Mapping to: Other
 */
export const otherInfoSchema = z
  .object({
    company_name: z
      .string()
      .trim()
      .max(100, '法人名は100文字以下にしてください。')
      .optional()
      .nullable(),
    clinic_rank: z.coerce.number().min(1).max(2).optional().nullable(),
    payment_type: optionalRangeDigits(
      1,
      11,
      '計算書区分は整数にしてください。',
      '計算書区分は1〜11桁にしてください。'
    ),
    claim_start_yearmonth: yearMonthSchema,
    claim_end_yearmonth: yearMonthSchema,
    monthly_fee: optionalRangeDigits(
      1,
      11,
      '会費は数字にしてください。',
      '会費は1〜11桁にしてください。- '
    ),
    is_company: z.coerce.boolean(),
    sales_staff_id: optionalRangeDigits(
      1,
      11,
      '営業担当者は数字にしてください。',
      '営業担当者は1〜11桁にしてください。'
    ),
    remarks: z.string().trim().max(100, '備考は100文字以下にしてください。').optional().nullable(),
  })
  .refine(
    data => {
      if (!data.claim_start_yearmonth || !data.claim_end_yearmonth) return true;
      return data.claim_start_yearmonth <= data.claim_end_yearmonth;
    },
    {
      message: '開始請求月は最終請求月以前の月を指定してください。',
      path: ['claim_start_yearmonth'],
    }
  );

/**
 * Complete Clinic Create Form Schema
 */
export const clinicCreateFormSchema = z.object({
  clinicInfo: clinicInfoSchema,
  clinicPaymentInfo: clinicPaymentInfoSchema,
  authorizedReceipt: authorizedReceiptSchema,
  receiptFeeTransfer: receiptFeeTransferSchema,
  otherInfo: otherInfoSchema,
});

/**
 * Type inference from schema
 */
export type ClinicInfoFormData = z.infer<typeof clinicInfoSchema>;
export type ClinicPaymentInfoFormData = z.infer<typeof clinicPaymentInfoSchema>;
export type AuthorizedReceiptFormData = z.infer<typeof authorizedReceiptSchema>;
export type ReceiptFeeTransferFormData = z.infer<typeof receiptFeeTransferSchema>;
export type OtherInfoFormData = z.infer<typeof otherInfoSchema>;
export type ClinicCreateFormData = z.infer<typeof clinicCreateFormSchema>;

/**
 * Flattens the nested ClinicCreateFormData into a single-level object.
 * Useful for API submissions where a flat structure is expected.
 */
export const flattenClinicCreateData = (data: ClinicCreateFormData) => {
  return {
    ...data.clinicInfo,
    ...data.clinicPaymentInfo,
    ...data.authorizedReceipt,
    ...data.receiptFeeTransfer,
    ...data.otherInfo,
  };
};
